/**
 * Lesson *progress persistence*.
 *
 * Progress is a single JSON blob in `localStorage`, keyed by
 * {@link PROGRESS_STORAGE_KEY}. The shape records, per chapter, which step ids
 * the kid has completed — that's enough to derive the menu's completion marker
 * (the `*`): a chapter is complete once every one of its steps is done.
 *
 * Everything here is pure + storage-injectable so it can be unit-tested in the
 * node environment with an in-memory stub (no real browser `localStorage`),
 * matching the rest of the scaffold's no-DOM testing philosophy.
 */
import type { Chapter } from "./content";

/** Versioned storage key so the schema can evolve without clobbering. */
export const PROGRESS_STORAGE_KEY = "sprocket.progress.v1";

/** The subset of the DOM `Storage` API this module needs. */
export interface ProgressStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Per-chapter completion record. */
export interface ChapterProgress {
  /** Ids of steps the kid has completed, in completion order. */
  completedStepIds: string[];
}

/** The whole persisted progress document. */
export interface ProgressState {
  /** Schema version, for forward-compatible migrations. */
  version: 1;
  /** Per-chapter progress, keyed by chapter id. */
  chapters: Record<string, ChapterProgress>;
}

/** Status of a single chapter — drives the menu's `done/total` + `*` marker. */
export interface ChapterStatus {
  /** How many of the chapter's steps are complete. */
  done: number;
  /** Total number of steps in the chapter. */
  total: number;
  /** True when every step is complete (the `*` marker). */
  complete: boolean;
}

/** A brand-new, empty progress document. */
export function emptyProgress(): ProgressState {
  return { version: 1, chapters: {} };
}

/**
 * Read progress from storage, tolerating absence and corruption: anything that
 * isn't a well-formed document yields a fresh empty state rather than throwing.
 */
export function loadProgress(storage: ProgressStorage): ProgressState {
  const raw = storage.getItem(PROGRESS_STORAGE_KEY);
  if (!raw) return emptyProgress();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isProgressState(parsed)) return emptyProgress();
    return parsed;
  } catch {
    return emptyProgress();
  }
}

/** Persist progress as JSON under the versioned key. */
export function saveProgress(
  storage: ProgressStorage,
  state: ProgressState,
): void {
  storage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(state));
}

/**
 * Return a new state with `stepId` marked complete in `chapterId`. Pure: the
 * input is never mutated, and marking an already-complete step is a no-op
 * (idempotent), so callers can mark freely.
 */
export function markStepComplete(
  state: ProgressState,
  chapterId: string,
  stepId: string,
): ProgressState {
  const existing = state.chapters[chapterId]?.completedStepIds ?? [];
  if (existing.includes(stepId)) return state;

  return {
    ...state,
    chapters: {
      ...state.chapters,
      [chapterId]: { completedStepIds: [...existing, stepId] },
    },
  };
}

/** Whether a given step has been completed. */
export function isStepComplete(
  state: ProgressState,
  chapterId: string,
  stepId: string,
): boolean {
  return state.chapters[chapterId]?.completedStepIds.includes(stepId) ?? false;
}

/**
 * Derive a chapter's completion status. Only steps that actually belong to the
 * chapter count, so stray/stale ids in storage never inflate the total.
 */
export function chapterCompletion(
  chapter: Chapter,
  state: ProgressState,
): ChapterStatus {
  const total = chapter.steps.length;
  const done = chapter.steps.filter((step) =>
    isStepComplete(state, chapter.id, step.id),
  ).length;
  return { done, total, complete: total > 0 && done === total };
}

/* ------------------------------- internals -------------------------------- */

/** Structural guard for stored JSON, so corrupt data is rejected cleanly. */
function isProgressState(value: unknown): value is ProgressState {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.version !== 1) return false;
  if (typeof v.chapters !== "object" || v.chapters === null) return false;
  return Object.values(v.chapters as Record<string, unknown>).every((entry) => {
    if (typeof entry !== "object" || entry === null) return false;
    const ids = (entry as Record<string, unknown>).completedStepIds;
    return Array.isArray(ids) && ids.every((id) => typeof id === "string");
  });
}
